import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useRef } from "react";
import { Animated, StyleSheet, Text, TouchableOpacity } from "react-native";

import { colors, fonts, shadows } from "@/lib/theme";

interface Props {
    count: number;
    onPress: () => void;
}

export function DiagnosisHubFAB({ count, onPress }: Props) {
    const badgeScale = useRef(new Animated.Value(0)).current;
    const prevCount = useRef(0);

    useEffect(() => {
        if (count > 0 && count !== prevCount.current) {
            // Bounce animation on count change
            badgeScale.setValue(0);
            Animated.spring(badgeScale, {
                toValue: 1,
                friction: 4,
                tension: 200,
                useNativeDriver: true
            }).start();
        } else if (count === 0) {
            badgeScale.setValue(0);
        }
        prevCount.current = count;
    }, [count, badgeScale]);

    return (
        <TouchableOpacity
            style={styles.fab}
            onPress={onPress}
            activeOpacity={0.85}
            accessibilityLabel={`Diagnosis hub, ${count} diagnoses`}
            accessibilityRole="button"
            accessibilityHint="Opens list of all diagnoses for this visit"
        >
            <MaterialCommunityIcons name="clipboard-text-outline" size={24} color="#FFFFFF" />

            {count > 0 && (
                <Animated.View
                    pointerEvents="none"
                    style={[
                        styles.badge,
                        {
                            transform: [
                                {
                                    scale: badgeScale.interpolate({
                                        inputRange: [0, 0.6, 1],
                                        outputRange: [0, 1.3, 1]
                                    })
                                }
                            ]
                        }
                    ]}
                >
                    <Text style={styles.badgeText}>{count > 99 ? "99+" : String(count)}</Text>
                </Animated.View>
            )}
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    fab: {
        position: "absolute",
        bottom: 24,
        right: 20,
        width: 52,
        height: 52,
        borderRadius: 26,
        backgroundColor: colors.teal500,
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
        zIndex: 50,
        ...shadows.teal
    },
    badge: {
        position: "absolute",
        top: -4,
        right: -4,
        minWidth: 22,
        height: 22,
        borderRadius: 11,
        backgroundColor: colors.pink500,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 4,
        borderWidth: 2.5,
        borderColor: colors.background,
        zIndex: 2,
        elevation: 6,
        shadowColor: "rgba(231,72,245,0.5)",
        shadowOpacity: 0.28,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 }
    },
    badgeText: {
        fontSize: 11,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF",
        includeFontPadding: false,
        textAlignVertical: "center"
    }
});
